import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';

import { fg } from '@atlaskit/platform-feature-flags';

import type {
	AppSwitcherApiAvailableProductsResponse,
	AppSwitcherApiOrg,
	AvailableWorkspacesResponse,
	OrgContextApi,
} from '../common/types/org-context';
import type { SpaceResponse, SpacesListResponse } from '../common/types/spaces';

declare global {
	interface Window {
		STUDIO_APP_VERSION: string;
	}
}

export const fetchOrgContextData = async (workspaceId: string): Promise<OrgContextApi> => {
	const response = await fetch(
		`/gateway/studio/workspace/${workspaceId}/api/studio/workspace/context`,
	);
	if (!response.ok) {
		throw new Error('Failed to fetch studio workspace context data');
	}
	return response.json();
};

export const orgContextQueryOptions = (workspaceId: string) =>
	queryOptions({
		queryKey: getOrgContextQueryKey(workspaceId),
		queryFn: () => fetchOrgContextData(workspaceId),
		enabled: !!workspaceId,
		staleTime: Infinity,
	});

export const fetchAvailableOrgs = async (): Promise<AppSwitcherApiAvailableProductsResponse> => {
	const response = await fetch('/gateway/api/app-switcher/api/available-products?source=studio');
	if (!response.ok) {
		throw new Error('Failed to fetch available organisations');
	}
	return response.json();
};

export const fetchAvailableWorkspaces = async (
	uniqueOrgs: AppSwitcherApiOrg[],
): Promise<AvailableWorkspacesResponse> => {
	const isAppSelectionChangeEnabled = fg('asf620_route_arch_and_app_selection_changes');
	const isGetOrgListInStudioBeEnabled = fg('asf-828-get-org-list-in-studio-be');
	const response = await fetch('/gateway/studio/api/studio/organization/workspace', {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
		},
		body:
			isAppSelectionChangeEnabled && isGetOrgListInStudioBeEnabled
				? uniqueOrgs.length > 0
					? JSON.stringify(uniqueOrgs.map((org) => org.id))
					: undefined
				: JSON.stringify(uniqueOrgs.map((org) => org.id)),
	});
	if (!response.ok) {
		throw new Error('Failed to fetch available workspaces');
	}
	return response.json();
};

export const getOrgContextQueryKey = (workspaceId?: string) => ['org-context', workspaceId];
export const getAvailableOrgsQueryKey = (): string[] => ['app-switcher-api-available-products'];
export const getAvailableWorkspacesQueryKey = (uniqueOrgs: AppSwitcherApiOrg[]): string[] => {
	const queryKey = ['studio-BE-available-workspaces', ...uniqueOrgs.map((org) => org.id)];
	return queryKey;
};
export const getWorkspaceIdBySiteIdQueryKey = (siteId: string): string[] => [
	'workspace-id-by-site-id',
	siteId,
];
export const getRecentWorkspaceForUserQueryKey = (atlassianAccountId: string): string[] => [
	'recent-workspace-for-user',
	atlassianAccountId,
];

export const fetchSpaceBySpaceId = async (
	workspaceId: string,
	spaceId: string,
): Promise<SpaceResponse> => {
	const response = await fetch(
		`/gateway/studio/workspace/${workspaceId}/api/studio/spaces/${spaceId}`,
	);
	if (!response.ok) {
		throw new Error('Failed to fetch space by space id');
	}
	return response.json();
};

export const getSpaceQueryKey = (workspaceId: string, spaceId: string): string[] => [
	'space',
	workspaceId,
	spaceId,
];
export const getSpaceQueryOptions = (workspaceId: string, spaceId: string) =>
	queryOptions({
		queryKey: getSpaceQueryKey(workspaceId, spaceId),
		queryFn: () => fetchSpaceBySpaceId(workspaceId, spaceId),
		enabled: Boolean(workspaceId) && Boolean(spaceId),
		staleTime: Infinity,
	});

export const fetchSpacesApi = async (
	workspaceId: string,
	opts: { query?: string; after?: string | null },
	errorMessage: string,
): Promise<SpacesListResponse> => {
	const params = new URLSearchParams();
	if (opts?.query) {
		params.set('query', opts.query);
	}
	if (opts?.after) {
		params.set('after', opts.after);
	}

	const response = await fetch(
		`/gateway/studio/workspace/${workspaceId}/api/studio/spaces?${params.toString()}`,
	);

	if (!response.ok) {
		throw new Error(errorMessage);
	}
	return response.json();
};

type SpacesPageParam = {
	cursor: string | null;
	search?: string;
};

export const getSpacesInfiniteQueryKey = (workspaceId: string, search?: string): string[] => [
	'spaces-infinite',
	workspaceId,
	search || '',
];

export const getSpacesInfiniteQueryOptions = (workspaceId: string, search?: string) =>
	infiniteQueryOptions({
		queryKey: getSpacesInfiniteQueryKey(workspaceId, search),
		queryFn: ({ pageParam }) =>
			fetchSpacesApi(
				workspaceId,
				{
					query: search,
					after: pageParam?.cursor ?? null,
				},
				'Failed to fetch spaces',
			),
		initialPageParam: null,
		getNextPageParam: (lastPage: SpacesListResponse) => {
			if (!lastPage.hasNext || !lastPage.nextCursor) {
				return null;
			}

			return {
				cursor: lastPage.nextCursor,
				search,
			} as SpacesPageParam;
		},
		staleTime: Infinity,
	});

export const createSpace = async (
	workspaceId: string,
	data: { name: string },
): Promise<SpaceResponse> => {
	const response = await fetch(`/gateway/studio/workspace/${workspaceId}/api/studio/spaces`, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
		},
		body: JSON.stringify(data),
	});
	if (!response.ok) {
		throw new Error(`Failed to create space: ${response.status} ${response.statusText}`);
	}
	return response.json();
};

export const getSpacesQueryKey = (workspaceId: string): string[] => ['spaces', workspaceId];

/**
 * Placeholder function to get workspace ID by site ID.
 * TODO: Replace with real API endpoint - https://product-fabric.atlassian.net/browse/ASF-910
 */
export const getWorkspaceIdBySiteId = async (_siteId: string): Promise<string | undefined> => {
	return undefined;
};

/**
 * Placeholder function to retrieve recent workspace for user.
 * TODO: Replace with real API endpoint - https://product-fabric.atlassian.net/browse/ASF-910
 */
export const getRecentWorkspaceForUser = async (_atlassianAccountId: string): Promise<string[]> => {
	return [];
};
